import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Button,
  Grid,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Timer as TimerIcon,
  CheckCircle as CheckIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, useSpring, useTransform } from 'framer-motion';
import confetti from 'canvas-confetti';
import MainLayout from '../../../components/layout/MainLayout';
import LeaderboardTable from '../../../components/quiz/LeaderboardTable';
import RatingRevealCard from '../../../components/quiz/live/RatingRevealCard';
import { getMyAttemptWithResponses, getQuizLeaderboard, getQuiz } from '../../../services/quizService';
import { AttemptWithResponses, QuizLeaderboardEntry, QuizWithQuestions } from '../../../types/quiz';

// Animated counter component
const AnimatedCounter: React.FC<{ value: number; duration?: number }> = ({ value, duration = 1500 }) => {
  const spring = useSpring(0, { duration });
  const display = useTransform(spring, (current) => Math.round(current));
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    spring.set(value);
    const unsubscribe = display.on('change', (latest) => {
      setDisplayValue(latest);
    });
    return () => unsubscribe();
  }, [value, spring, display]);

  return <>{displayValue}</>;
};

function QuizResultsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const quizId = Number(id);

  const [attemptData, setAttemptData] = useState<AttemptWithResponses | null>(null);
  const [quiz, setQuiz] = useState<QuizWithQuestions | null>(null);
  const [leaderboard, setLeaderboard] = useState<QuizLeaderboardEntry[]>([]);
  const [userRank, setUserRank] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasNavigatedRef = React.useRef(false);

  const triggerCelebration = (rank: number) => {
    // Gold confetti for 1st place
    if (rank === 1) {
      const duration = 3000;
      const end = Date.now() + duration;

      const frame = () => {
        confetti({
          particleCount: 5,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.6 },
          colors: ['#FFD700', '#FFA500', '#FF8C00'],
        });
        confetti({
          particleCount: 5,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.6 },
          colors: ['#FFD700', '#FFA500', '#FF8C00'],
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };
      frame();
    }
    // Silver confetti for 2nd place
    else if (rank === 2) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#C0C0C0', '#A9A9A9', '#D3D3D3'],
      });
    }
    // Bronze confetti for 3rd place
    else if (rank === 3) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#CD7F32', '#B87333', '#966F33'],
      });
    }
  };

  const loadResults = useCallback(async () => {
    try {
      setLoading(true);

      console.log('[QuizResultsPage] Loading results for quiz', quizId);

      const [attemptResult, quizData, leaderboardData] = await Promise.all([
        getMyAttemptWithResponses(quizId),
        getQuiz(quizId),
        getQuizLeaderboard(quizId),
      ]);

      console.log('[QuizResultsPage] attemptResult:', attemptResult);
      console.log('[QuizResultsPage] attemptResult type:', typeof attemptResult);
      console.log('[QuizResultsPage] attemptResult keys:', attemptResult ? Object.keys(attemptResult) : 'null');
      console.log('[QuizResultsPage] attemptResult.completedAt:', attemptResult?.completedAt);

      if (!attemptResult || !attemptResult.completedAt) {
        console.log('[QuizResultsPage] No completed attempt, navigating to play page');
        if (!hasNavigatedRef.current) {
          hasNavigatedRef.current = true;
          navigate(`/student/quizzes/${quizId}/play`);
        }
        return;
      }

      console.log('[QuizResultsPage] Setting attempt data');
      setAttemptData(attemptResult);
      setQuiz(quizData);
      setLeaderboard(leaderboardData);

      // Find user's rank from the leaderboard data (it already includes rank from backend)
      const userEntry = leaderboardData.find((entry) => entry.userId === attemptResult.userId);
      const actualRank = userEntry ? userEntry.rank : 0;
      setUserRank(actualRank);

      setLoading(false);

      // Trigger celebration after a short delay
      if (actualRank <= 3) {
        setTimeout(() => triggerCelebration(actualRank), 500);
      }
    } catch (err: unknown) {
      console.error('Error loading results:', err);
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to load results');
      setLoading(false);
    }
  }, [quizId, navigate]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate time taken in seconds
  const getTimeTakenSeconds = (): number => {
    if (!attemptData || !attemptData.startedAt || !attemptData.completedAt) return 0;
    const start = new Date(attemptData.startedAt).getTime();
    const end = new Date(attemptData.completedAt).getTime();
    return Math.floor((end - start) / 1000);
  };

  // Count actual correct answers from responses
  const getCorrectAnswersCount = (): number => {
    if (!attemptData) return 0;
    return attemptData.responses.filter((response) => response.isCorrect).length;
  };

  const getPercentage = (): number => {
    if (!attemptData) return 0;
    const correct = getCorrectAnswersCount();
    return Math.round((correct / attemptData.totalQuestions) * 100);
  };

  const getRankMessage = (): string => {
    if (userRank === 0) return 'Ranking not available';
    if (userRank === 1) return '🥇 You placed 1st! Amazing!';
    if (userRank === 2) return '🥈 You placed 2nd! Great job!';
    if (userRank === 3) return '🥉 You placed 3rd! Well done!';
    return `You placed #${userRank}`;
  };

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (error || !attemptData || !quiz) {
    return (
      <MainLayout>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Alert severity="error">{error || 'Failed to load results'}</Alert>
          <Button variant="contained" onClick={() => navigate('/student/quizzes')} sx={{ mt: 2 }}>
            Back to Quizzes
          </Button>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            Quiz Complete!
          </Typography>
          <Typography variant="h5" color="text.secondary">
            {quiz.title}
          </Typography>
        </Box>

        {/* Score Display */}
        <motion.div
          initial={{ scale: 0.5, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 100, damping: 15 }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              mb: 4,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}
          >
            <motion.div
              animate={{ rotate: [0, -10, 10, -10, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <TrophyIcon sx={{ fontSize: 80, color: '#FFD700', mb: 2 }} />
            </motion.div>
            <Typography variant="h2" fontWeight="bold" gutterBottom sx={{ color: 'white' }}>
              <AnimatedCounter value={attemptData.score} /> Points
            </Typography>
            <Typography variant="h5" gutterBottom sx={{ color: 'rgba(255,255,255,0.95)' }}>
              <AnimatedCounter value={getCorrectAnswersCount()} /> / {attemptData.totalQuestions} Correct ({getPercentage()}%)
            </Typography>
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', mt: 2 }}>
              {getRankMessage()}
            </Typography>
          </Paper>
        </motion.div>

        {/* Rating reveal — only when this attempt is tied to a live room, e.g.
            a late-entrant replay of a missed live quiz. An ordinary
            self-paced attempt has no roomId and never rated. */}
        {attemptData.roomId != null && (
          <Box sx={{ mb: 4 }}>
            <RatingRevealCard roomId={attemptData.roomId} />
          </Box>
        )}

        {/* Stats Grid */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold">
                    <AnimatedCounter value={getCorrectAnswersCount()} duration={1000} />
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Correct Answers
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={4}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <TimerIcon sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold">
                    {formatTime(getTimeTakenSeconds())}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Time Taken
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={4}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <motion.div
                    animate={
                      userRank <= 3
                        ? { rotate: [0, -15, 15, -15, 15, 0], scale: [1, 1.2, 1] }
                        : {}
                    }
                    transition={{ duration: 0.8, delay: 1 }}
                  >
                    <TrophyIcon
                      sx={{
                        fontSize: 48,
                        color: userRank === 1 ? '#FFD700' : userRank === 2 ? '#C0C0C0' : userRank === 3 ? '#CD7F32' : 'warning.main',
                        mb: 1,
                      }}
                    />
                  </motion.div>
                  <Typography variant="h4" fontWeight="bold">
                    #{userRank}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Your Rank
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Leaderboard */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Leaderboard
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            See how you rank against other participants
          </Typography>
          <LeaderboardTable entries={leaderboard} maxEntries={50} />
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/student/quizzes')}
          >
            Back to Quizzes
          </Button>
        </Box>
      </Container>
    </MainLayout>
  );
}

export default QuizResultsPage;
